import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from '@reflex/ui/components/ui/collapsible';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';

interface CollapsibleSectionProps {
  title: ReactNode;
  /** Optional icon shown to the left of the title. */
  icon?: ReactNode;
  /** Whether the section starts open. Defaults to `true`. */
  defaultOpen?: boolean;
  className?: string;
  children: ReactNode;
}

/**
 * Section heading with a chevron toggle that expands/collapses its body. Used
 * by detail panels (Blueprints, Agents) to break up long forms or read-only
 * surfaces into named, scrollable groups.
 */
export function CollapsibleSection({
  title,
  icon,
  defaultOpen = true,
  className,
  children,
}: CollapsibleSectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn('space-y-3', className)}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="group/trigger flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 -mx-2 text-left outline-none transition-colors hover:bg-muted/40 focus-visible:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring/50"
        >
          <ChevronDown
            className={cn(
              'h-3 w-3 shrink-0 text-muted-foreground/40 transition-transform duration-200 group-hover/trigger:text-muted-foreground/70',
              !open && '-rotate-90',
            )}
          />
          {icon}
          {/* Detail-panel section headers read as content headings, so they
              use the stronger SectionLabel tone rather than the muted default
              used for sidebar list-group labels. */}
          <SectionLabel as="span" tone="strong">
            {title}
          </SectionLabel>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>{children}</CollapsibleContent>
    </Collapsible>
  );
}
